// File: frontend/src/components/User/UserTable.tsx
// Enhanced component with full user management capabilities

import React from 'react';
import { Users,  } from 'lucide-react';
import { UserRow } from './UserRow';
import { Pagination } from '@/components/common/ui';
import { User } from '@/lib/api/userService';

interface UserTableProps {
  users: User[];
  isLoading: boolean;
  searchTerm: string;
  roleFilter: string;
  statusFilter: string;
  pagination?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
    has_more: boolean;
  };
  currentPage: number;
  onPageChange: (page: number) => void;
  onToggleStatus: (id: number) => void;
  onDeleteUser: (id: number) => void;
  onUpdateRole: (id: number, role: string) => void;
  onResetPassword: (id: number) => void;
  isTogglingStatus: boolean;
  isDeletingUser: boolean;
  isUpdatingRole: boolean;
  isResettingPassword: boolean;
  selectedUsers?: number[];
  onSelectUser?: (id: number, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users = [],
  isLoading,
  searchTerm,
  roleFilter,
  statusFilter,
  pagination,
  currentPage,
  onPageChange,
  onToggleStatus,
  onDeleteUser,
  onUpdateRole,
  onResetPassword,
  isTogglingStatus,
  isDeletingUser,
  isUpdatingRole,
  isResettingPassword,
  selectedUsers = [],
  onSelectUser,
  onSelectAll,
}) => {
  const allSelected = users.length > 0 && selectedUsers.length === users.length;
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < users.length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Users</h3>
          {users.length > 0 && (
            <div className="text-sm text-gray-500">
              Showing {pagination?.from || 1} to {pagination?.to || users.length} of {pagination?.total || users.length} users
            </div>
          )}
        </div>
      </div>
      
      {isLoading ? (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      ) : users.length > 0 ? (
        <>
          {/* Mobile/Tablet Card View */}
          <div className="block xl:hidden">
            <div className="space-y-3 p-4">
              {users.map((user) => (
                <UserRow
                  key={user.user_id}
                  user={user}
                  onToggleStatus={onToggleStatus}
                  onDeleteUser={onDeleteUser}
                  onUpdateRole={onUpdateRole}
                  onResetPassword={onResetPassword}
                  isTogglingStatus={isTogglingStatus}
                  isDeletingUser={isDeletingUser}
                  isUpdatingRole={isUpdatingRole}
                  isResettingPassword={isResettingPassword}
                  isSelected={selectedUsers.includes(user.user_id)}
                  onSelect={(selected) => onSelectUser?.(user.user_id, selected)}
                  isMobile={true}
                />
              ))}
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden xl:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={(e) => onSelectAll?.(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Verified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <UserRow
                    key={user.user_id}
                    user={user}
                    onToggleStatus={onToggleStatus}
                    onDeleteUser={onDeleteUser}
                    onUpdateRole={onUpdateRole}
                    onResetPassword={onResetPassword}
                    isTogglingStatus={isTogglingStatus}
                    isDeletingUser={isDeletingUser}
                    isUpdatingRole={isUpdatingRole}
                    isResettingPassword={isResettingPassword}
                    isSelected={selectedUsers.includes(user.user_id)}
                    onSelect={(selected) => onSelectUser?.(user.user_id, selected)}
                    isMobile={false}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && (
            <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
              <Pagination
                currentPage={pagination.current_page || 1}
                totalPages={pagination.total_pages || 1}
                totalItems={pagination.total || 0}
                itemsPerPage={pagination.per_page || 10}
                onPageChange={onPageChange}
                variant="default"
                size="md"
              />
            </div>
          )}
        </>
      ) : (
        <div className="p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || roleFilter || statusFilter
              ? 'Try adjusting your search criteria or filters.'
              : 'No users are currently available.'}
          </p>
          {(searchTerm || roleFilter || statusFilter) && (
            <button
              onClick={() => {
                // This would typically call a clear filters function
                // For now, we'll just show the message
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UserTable;